<script setup>
// Усі кліпи: з ефірів і з записів. Сортування — нові, популярні (за
// лайками), кращі за тиждень, за переглядами, старі; фільтри — джерело,
// аніме, пошук за назвою і «мої». Стан фільтрів живе в адресі, тож посилання
// «популярні кліпи з Магічної битви» можна скинути комусь як є.

const route = useRoute()
const router = useRouter()
const config = useRuntimeConfig()
const token = useStrapiToken()
const user = useStrapiUser()

const PAGE = 24

const SORTS = [
  { id: 'new', label: 'Нові' },
  { id: 'top', label: 'Популярні' },
  { id: 'week', label: 'Кращі за тиждень' },
  { id: 'views', label: 'Перегляди' },
  { id: 'old', label: 'Старі' }
]

const SOURCES = [
  { id: '', label: 'Усі' },
  { id: 'live', label: 'З ефірів' },
  { id: 'anime', label: 'З записів' },
  { id: 'archive', label: 'З архіву ефірів' }
]

const sort = computed(() => (SORTS.some((s) => s.id === route.query.sort) ? route.query.sort : 'new'))
const source = computed(() => (['live', 'anime', 'archive'].includes(route.query.source) ? route.query.source : ''))
const anime = computed(() => String(route.query.anime || ''))
const mine = computed(() => route.query.author === 'me')
const search = ref(String(route.query.q || ''))

const setQuery = (patch) => {
  const query = { ...route.query, ...patch }
  for (const key of Object.keys(query)) if (query[key] === '' || query[key] == null) delete query[key]
  router.replace({ query })
}

let searchTimer = null
watch(search, (value) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => setQuery({ q: value.trim() }), 350)
})

const clips = ref([])
const total = ref(0)
const loading = ref(true)
const loadingMore = ref(false)
const failed = ref(false)

const params = (offset) => ({
  sort: sort.value,
  source: source.value || undefined,
  anime: anime.value || undefined,
  search: String(route.query.q || '') || undefined,
  author: mine.value ? 'me' : undefined,
  offset,
  limit: PAGE
})

const fetchPage = (offset) =>
  $fetch(`${config.public.apiBase}/api/clips`, {
    query: params(offset),
    headers: token.value ? { Authorization: `Bearer ${token.value}` } : {}
  })

let generation = 0
const load = async () => {
  const mineGen = ++generation
  loading.value = true
  try {
    const data = await fetchPage(0)
    if (mineGen !== generation) return
    clips.value = data.clips
    total.value = data.total
    failed.value = false
  } catch (e) {
    if (mineGen === generation) failed.value = true
  } finally {
    if (mineGen === generation) loading.value = false
  }
}

const loadMore = async () => {
  if (loadingMore.value || clips.value.length >= total.value) return
  loadingMore.value = true
  try {
    const data = await fetchPage(clips.value.length)
    const seen = new Set(clips.value.map((c) => c.id))
    clips.value = [...clips.value, ...data.clips.filter((c) => !seen.has(c.id))]
    total.value = data.total
  } finally {
    loadingMore.value = false
  }
}

watch(() => route.query, load, { deep: true })
onMounted(load)

// Нескінченна стрічка: дійшли до низу — наступна сторінка
const sentinel = ref(null)
useIntersectionObserver(sentinel, ([entry]) => {
  if (entry?.isIntersecting) loadMore()
}, { rootMargin: '600px' })

useHead({ title: 'Кліпи — Олсіор дивиться аніме' })
useSeoMeta({
  description: 'Кліпи з ефірів і записів переглядів аніме Olsior: найкращі моменти, які вирізали глядачі.'
})
</script>

<template>
  <div class="mx-auto px-[10px] py-[20px] max-w-[1350px] min-h-screen">
    <div class="flex flex-wrap items-baseline gap-3 mb-[6px]">
      <h1 class="font-bold text-2xl">Кліпи</h1>
      <span
        v-if="total"
        class="text-sm text-base-content/50"
        >{{ total }}</span
      >
    </div>
    <p class="mb-[16px] text-sm text-base-content/50">
      Моменти з ефірів і записів, які вирізали глядачі. Зробити свій — кнопка
      «Кліп» у плеєрі серії або ефіру.
    </p>

    <!-- Сортування й фільтри -->
    <div class="flex flex-wrap items-center gap-2 mb-[16px]">
      <div class="inline-flex flex-wrap gap-1 bg-neutral/60 p-1 rounded-lg">
        <button
          v-for="s in SORTS"
          :key="s.id"
          class="px-3 py-1 rounded-md text-sm transition-colors"
          :class="sort === s.id ? 'bg-primary text-primary-content' : 'text-base-content/70 hover:bg-base-content/10'"
          @click="setQuery({ sort: s.id === 'new' ? '' : s.id })"
        >
          {{ s.label }}
        </button>
      </div>

      <div class="inline-flex flex-wrap gap-1 bg-neutral/60 p-1 rounded-lg">
        <button
          v-for="s in SOURCES"
          :key="s.id || 'all'"
          class="px-3 py-1 rounded-md text-sm transition-colors"
          :class="source === s.id ? 'bg-primary text-primary-content' : 'text-base-content/70 hover:bg-base-content/10'"
          @click="setQuery({ source: s.id })"
        >
          {{ s.label }}
        </button>
      </div>

      <button
        v-if="user"
        class="btn btn-sm"
        :class="mine ? 'btn-primary' : 'btn-ghost'"
        @click="setQuery({ author: mine ? '' : 'me' })"
      >
        Мої кліпи
      </button>

      <span
        v-if="anime"
        class="gap-1 badge badge-lg"
      >
        {{ clips[0]?.animeTitle || anime }}
        <button
          title="Прибрати фільтр"
          @click="setQuery({ anime: '' })"
        >
          ✕
        </button>
      </span>

      <span class="flex-1" />

      <input
        v-model="search"
        type="search"
        class="bg-neutral/60 w-full sm:w-64 input input-sm"
        placeholder="Пошук за назвою"
      />
    </div>

    <div
      v-if="loading && !clips.length"
      class="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <div
        v-for="n in 8"
        :key="n"
        class="bg-neutral/50 rounded-[12px] aspect-video animate-pulse"
      />
    </div>

    <p
      v-else-if="failed"
      class="py-[40px] text-base-content/50 text-center"
    >
      Не вдалося завантажити кліпи
    </p>

    <div
      v-else-if="!clips.length"
      class="flex flex-col items-center gap-2 py-[60px] text-center"
    >
      <p class="text-base-content/60">
        {{ mine ? 'Ви ще не зробили жодного кліпу' : 'Кліпів ще немає' }}
      </p>
      <p class="text-sm text-base-content/40">
        Відкрийте будь-яку серію чи ефір і натисніть «Кліп» у плеєрі.
      </p>
    </div>

    <div
      v-else
      class="gap-x-4 gap-y-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      :class="{ 'opacity-60': loading }"
    >
      <ClipCard
        v-for="clip in clips"
        :key="clip.id"
        :clip="clip"
      />
    </div>

    <div
      ref="sentinel"
      class="h-10"
    />
    <p
      v-if="loadingMore"
      class="text-center"
    >
      <span class="loading loading-spinner loading-sm" />
    </p>
  </div>
</template>
